<template>
  <div class="sudoku-board">
    <div
      v-for="(cell, index) in board"
      :key="index"
      class="sudoku-cell"
      :class="{
        'cell-original': cell.isOriginal,
        'cell-selected': selectedCell === index,
        'cell-same-row': !cell.isOriginal && selectedCell !== null && Math.floor(selectedCell / 9) === Math.floor(index / 9),
        'cell-same-col': !cell.isOriginal && selectedCell !== null && selectedCell % 9 === index % 9,
        'cell-same-box': !cell.isOriginal && selectedCell !== null && getSameBo(selectedCell, index),
        'cell-incorrect': !cell.isOriginal && cell.value !== null && solution[index] !== cell.value
      }"
      @click="() => selectCell(index)"
      @keydown="(e) => handleKeydown(e, index)"
    >
      <div class="cell-content">
        <span v-if="cell.value" class="cell-number">{{ cell.value }}</span>
      </div>
      <div
        v-if="index % 9 === 8"
        :style="{ gridColumn: '1 / -1', height: '1px', background: 'var(--color-text-primary, #1F2937)' }"
      ></div>
      <div
        v-if="(index + 1) % 27 === 0"
        :style="{ gridColumn: '1 / -1', height: '2px', background: 'var(--color-text-primary, #1F2937)' }"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'

const props = defineProps({
  board: {
    type: Array,
    required: true
  },
  solution: {
    type: Array,
    required: true
  },
  selectedCell: {
    type: [Number, null],
    default: null
  }
})

const emit = defineEmits(['select-cell', 'update-cell'])

// 检查两个格子是否在同一个3x3方块
const getSameBo = (index1, index2) => {
  const row1 = Math.floor(index1 / 9)
  const col1 = index1 % 9
  const row2 = Math.floor(index2 / 9)
  const col2 = index2 % 9

  const box1 = Math.floor(row1 / 3) * 3 + Math.floor(col1 / 3)
  const box2 = Math.floor(row2 / 3) * 3 + Math.floor(col2 / 3)

  return box1 === box2
}

// 选择格子
const selectCell = (index) => {
  emit('select-cell', index)
}

// 处理键盘输入
const handleKeydown = (event, index) => {
  const key = event.key

  // 数字键 1-9
  if (/^[1-9]$/.test(key)) {
    event.preventDefault()
    emit('update-cell', index, parseInt(key))
  }

  // 删除键
  if (key === 'Delete' || key === 'Backspace') {
    event.preventDefault()
    emit('update-cell', index, null)
  }

  // 方向键
  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
    event.preventDefault()
    let newIndex = index
    const row = Math.floor(index / 9)
    const col = index % 9

    switch (key) {
      case 'ArrowUp':
        newIndex = Math.max(0, index - 9)
        break
      case 'ArrowDown':
        newIndex = Math.min(80, index + 9)
        break
      case 'ArrowLeft':
        newIndex = col > 0 ? index - 1 : index
        break
      case 'ArrowRight':
        newIndex = col < 8 ? index + 1 : index
        break
    }

    emit('select-cell', newIndex)
  }
}
</script>

<style scoped>
.sudoku-board {
  display: grid;
  grid-template-columns: repeat(9, 50px);
  grid-template-rows: repeat(9, 50px);
  gap: 0;
  background-color: var(--color-text-primary, #1F2937);
  padding: 2px;
  border-radius: 8px;
  overflow: hidden;
  user-select: none;
}

.sudoku-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-card);
  border: 1px solid #D1D5DB;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  font-weight: 700;
  font-size: 18px;
  color: var(--color-text-primary, #1F2937);
}

/* 原始数字格子 */
.cell-original {
  background: var(--color-bg-light, #F9FAFB);
  cursor: default;
  font-weight: 800;
  color: var(--color-text-primary, #1F2937);
}

/* 选中的格子 */
.cell-selected {
  background: rgba(255, 107, 53, 0.15);
  border-color: var(--color-primary-orange, #FF6B35);
  border-width: 2px;
  z-index: 10;
}

/* 同一行/列/方块的格子 */
.cell-same-row,
.cell-same-col,
.cell-same-box {
  background: rgba(255, 107, 53, 0.08);
}

/* 错误的格子 */
.cell-incorrect {
  background: #FEE2E2;
  color: var(--color-danger, #EF4444);
}

/* 边界线 */
.sudoku-cell:nth-child(9n) {
  border-right: 2px solid var(--color-text-primary, #1F2937);
}

.sudoku-cell:nth-child(n + 19):nth-child(-n + 27),
.sudoku-cell:nth-child(n + 46):nth-child(-n + 54),
.sudoku-cell:nth-child(n + 73):nth-child(-n + 81) {
  border-bottom: 2px solid var(--color-text-primary, #1F2937);
}

.sudoku-cell:nth-child(3n) {
  border-right: 2px solid var(--color-text-primary, #1F2937);
}

.sudoku-cell:nth-child(3n + 1):nth-child(n + 1):nth-child(-n + 3),
.sudoku-cell:nth-child(3n + 1):nth-child(n + 28):nth-child(-n + 30),
.sudoku-cell:nth-child(3n + 1):nth-child(n + 55):nth-child(-n + 57),
.sudoku-cell:nth-child(3n + 1):nth-child(n + 73):nth-child(-n + 75) {
  border-right: 2px solid var(--color-text-primary, #1F2937);
}

/* 悬停效果 */
.sudoku-cell:hover:not(.cell-original) {
  background: rgba(255, 107, 53, 0.1);
}

.cell-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell-number {
  line-height: 1;
}

/* 响应式 */
@media (max-width: 768px) {
  .sudoku-board {
    grid-template-columns: repeat(9, 40px);
    grid-template-rows: repeat(9, 40px);
  }

  .sudoku-cell {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .sudoku-board {
    grid-template-columns: repeat(9, 32px);
    grid-template-rows: repeat(9, 32px);
  }

  .sudoku-cell {
    font-size: 14px;
  }
}
</style>
